import React from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import AnimatedGlobe from "@/components/AnimatedGlobe";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck, Globe2, Activity, Zap, FileCheck2, Hexagon,
  Building2, Banknote, Network, Sparkles, CircleCheck,
} from "lucide-react";

function StatTile({ k, v, sub }) {
  return (
    <div className="border-l border-white/15 pl-5 py-2">
      <div className="font-mono-fin text-[11px] uppercase tracking-widest text-white/50">{k}</div>
      <div className="font-display text-3xl text-white mt-1">{v}</div>
      {sub && <div className="text-xs text-white/60 mt-1">{sub}</div>}
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  return (
    <div className="bg-cosmos text-white" data-testid="landing-page">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 border border-white/20 px-3 py-1.5 rounded-full text-xs font-mono-fin text-white/80 bg-white/5 backdrop-blur"
                data-testid="hero-badge"
              >
                <span className="block w-1.5 h-1.5 bg-cyan-400 rounded-full pulse-dot"></span>
                {t("landing.kicker")}
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.05 }}
                className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter text-white mt-6 leading-[0.95]"
              >
                <Trans i18nKey="landing.title" components={{ em: <span className="brand-text-light" /> }} />
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="text-white/70 text-lg max-w-xl mt-6 leading-relaxed"
              >
                {t("landing.subtitle")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <Link to="/registro" className="btn-primary" data-testid="hero-cta-register">
                  {t("landing.cta_open")} <ArrowRight size={18} />
                </Link>
                <Link to="/login" className="btn-secondary" data-testid="hero-cta-login">
                  {t("landing.cta_login")}
                </Link>
              </motion.div>

              <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-y-2">
                <StatTile k={t("landing.stat_settlement")} v="< 10s" sub="SCT Inst" />
                <StatTile k={t("landing.stat_coverage")} v="36" sub={t("landing.stat_coverage_sub")} />
                <StatTile k={t("landing.stat_uptime")} v="99.99%" sub={t("landing.stat_uptime_sub")} />
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.15 }}
                className="relative w-full"
                style={{ aspectRatio: "1 / 1" }}
              >
                <AnimatedGlobe />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR / marquee */}
      <section className="border-y border-white/10 bg-white/5 overflow-hidden backdrop-blur">
        <div className="marquee-track py-5 text-white/60 font-mono-fin text-xs uppercase tracking-widest whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center">
              {["FCA · UK", "MiCA · EU", "PSD2", "SEPA SCT Inst", "ISO 20022", "GDPR", "EBA Guidelines", "AML / KYC"].map((t) => (
                <span key={t + i} className="px-10 flex items-center gap-3">
                  <Hexagon size={12} className="text-cyan-400" /> {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="productos" className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <div className="text-xs font-mono-fin text-cyan-400 uppercase tracking-widest mb-3">{t("landing.products_eyebrow")}</div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-white leading-tight whitespace-pre-line">
                {t("landing.products_title")}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 text-white/70 leading-relaxed text-lg">
              {t("landing.products_lead")}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-3xl overflow-hidden">
            {[
              { icon: Building2, t: t("landing.p_iban_t"), d: t("landing.p_iban_d") },
              { icon: Zap, t: t("landing.p_instant_t"), d: t("landing.p_instant_d") },
              { icon: Banknote, t: t("landing.p_std_t"), d: t("landing.p_std_d") },
              { icon: Network, t: t("landing.p_cbu_t"), d: t("landing.p_cbu_d") },
              { icon: ShieldCheck, t: t("landing.p_val_t"), d: t("landing.p_val_d") },
              { icon: FileCheck2, t: t("landing.p_doc_t"), d: t("landing.p_doc_d") },
            ].map(({ icon: I, t: tt, d }, i) => (
              <div key={i} className="bg-slate-950/60 backdrop-blur p-8 hover:bg-purple-900/30 transition-colors">
                <I size={22} className="text-cyan-400" />
                <div className="font-display font-bold text-xl text-white mt-5">{tt}</div>
                <div className="text-white/65 text-sm mt-2 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEFI STRIP */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs font-mono-fin text-blue-400 uppercase tracking-widest mb-3">{t("landing.defi_eyebrow")}</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter leading-tight">
              <Trans i18nKey="landing.defi_title" components={{ em: <span className="text-blue-400" /> }} />
            </h2>
            <p className="text-slate-300 mt-6 text-lg leading-relaxed max-w-xl">
              {t("landing.defi_lead")}
            </p>
            <div className="mt-8 space-y-3">
              {[t("landing.defi_b1"), t("landing.defi_b2"), t("landing.defi_b3")].map((x) => (
                <div key={x} className="flex items-center gap-3 text-slate-200">
                  <CircleCheck size={18} className="text-blue-400" /> <span className="text-sm">{x}</span>
                </div>
              ))}
            </div>
            <Link to="/arquitectura-defi" className="inline-flex items-center gap-2 text-blue-400 mt-8 hover:gap-3 transition-all" data-testid="link-defi-detail">
              {t("landing.defi_link")} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-slate-700 p-6 bg-slate-950">
              <div className="font-mono-fin text-[11px] uppercase tracking-widest text-slate-500 mb-3">Bloque #284.119</div>
              <div className="space-y-2 font-mono-fin text-xs">
                {[
                  ["hash", "0x9f3a…b8c2"],
                  ["tx", "TX-7A2F1B9C"],
                  ["amount", "€ 1.250,00"],
                  ["from", "ES91 2100 …1332"],
                  ["to",   "DE89 3704 …4321"],
                  ["status", "settled · 7.2s"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-800 py-2">
                    <span className="text-slate-500">{k}</span>
                    <span className="text-slate-100">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 text-blue-400 text-xs">
                <Activity size={14} /> Firma verificada · SHA-256
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section id="cumplimiento" className="border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="text-xs font-mono-fin text-cyan-400 uppercase tracking-widest mb-3">{t("landing.comp_eyebrow")}</div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-white leading-tight whitespace-pre-line">
                {t("landing.comp_title")}
              </h2>
              <p className="text-white/70 text-lg mt-5">
                {t("landing.comp_lead")}
              </p>
            </div>
            <div className="lg:col-span-7 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-3xl overflow-hidden">
              {[
                { t: "FCA", d: "Financial Conduct Authority · Reino Unido. Ref. 9XX834 (demo)." },
                { t: "MiCA", d: "Reglamento (UE) 2023/1114 sobre criptoactivos." },
                { t: "PSD2 + SCA", d: "Autenticación reforzada y open banking europeo." },
                { t: "GDPR", d: "Protección de datos personales bajo normativa europea." },
              ].map(({ t, d }) => (
                <div key={t} className="bg-slate-950/60 backdrop-blur p-7">
                  <div className="font-display font-bold text-2xl text-white">{t}</div>
                  <div className="text-sm text-white/65 mt-2 leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* B2B TEASER */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs font-mono-fin text-cyan-400 uppercase tracking-widest mb-3">{t("landing.b2b_eyebrow")}</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter text-white leading-tight">
              <Trans i18nKey="landing.b2b_title" components={{ em: <span className="brand-text-light" /> }} />
            </h2>
            <p className="text-white/70 mt-5 text-lg max-w-2xl">
              {t("landing.b2b_lead")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/empresas" className="btn-primary" data-testid="landing-b2b-cta">
                {t("landing.b2b_cta")} <ArrowRight size={18} />
              </Link>
              <Link to="/empresas#solicitar-api" className="btn-secondary" data-testid="landing-b2b-api">
                {t("landing.b2b_api")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-6">
              <div className="font-mono-fin text-xs uppercase tracking-widest text-white/60">Ejemplo · 500 transferencias/mes · 2.500€</div>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="font-display font-black text-5xl brand-text-light">€ 8.040</span>
                <span className="text-white/70 text-sm">ahorro / año</span>
              </div>
              <div className="mt-4 text-sm text-white/65">
                vs. comisión del 0,65% (banca tradicional EU).
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #4F46E5 0%, #6366F1 50%, #8B5CF6 100%)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter leading-tight text-white">
              {t("landing.cta_final_title")}
            </h2>
            <p className="text-white/85 mt-4 text-lg max-w-2xl">
              {t("landing.cta_final_lead")}
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/registro"
                  className="inline-flex items-center gap-2 bg-white text-indigo-700 font-bold px-7 py-4 rounded-full hover:bg-slate-900 hover:text-white transition-colors shadow-xl"
                  data-testid="cta-final-register">
              <Sparkles size={18} /> {t("landing.cta_final_btn")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
